import { useQuery } from '@tanstack/react-query';
import { USE_MOCK, analyticsApi, retryOnTransientError } from '@/api/client';
import { reportApiError } from '@/utils/errorReporter';
import type { PersonalGrowthResponse } from '@/api/types';

/** Fecha ISO (solo la parte de calendario) a `days` días antes de hoy. */
function daysAgoIso(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
}

// Mock growth data — monthly trend for the demo user.
// SIMPLIFICACIÓN: en la serie mensual real cada fundamental trae su propio conteo de
// evaluaciones (puede recibir menos respuestas que el total del mes); aquí, por
// brevedad del mock, todos los fundamentales repiten el `evaluations` del mes.
const MOCK_GROWTH: PersonalGrowthResponse = {
  evaluatedId: 'demo-user',
  // Ventanas móviles de 30 días — independientes de `history`, que sigue siendo mensual.
  today: {
    startDate: daysAgoIso(30),
    endDate: daysAgoIso(0),
    overallAverage: 3.4,
    evaluations: 8,
    fundamentals: [
      { fundamentalId: 'learner', average: 3.5, evaluations: 8 },
      { fundamentalId: 'player-owner', average: 3.3, evaluations: 8 },
      { fundamentalId: 'communicator', average: 3.4, evaluations: 8 },
      { fundamentalId: 'team-builder', average: 3.4, evaluations: 8 },
    ],
    attitudes: [],
  },
  threeMonthsAgo: {
    startDate: daysAgoIso(120),
    endDate: daysAgoIso(90),
    overallAverage: 2.9,
    evaluations: 6,
    fundamentals: [
      { fundamentalId: 'learner', average: 3.0, evaluations: 6 },
      { fundamentalId: 'player-owner', average: 2.8, evaluations: 6 },
      { fundamentalId: 'communicator', average: 2.9, evaluations: 6 },
      { fundamentalId: 'team-builder', average: 2.9, evaluations: 6 },
    ],
    attitudes: [],
  },
  history: [
    { month: '2025-07', overallAverage: 2.1, evaluations: 3, attitudes: [], fundamentals: [{ fundamentalId: 'learner', average: 2.0, evaluations: 3 }, { fundamentalId: 'player-owner', average: 2.3, evaluations: 3 }, { fundamentalId: 'communicator', average: 2.1, evaluations: 3 }, { fundamentalId: 'team-builder', average: 2.0, evaluations: 3 }] },
    { month: '2025-08', overallAverage: 2.4, evaluations: 4, attitudes: [], fundamentals: [{ fundamentalId: 'learner', average: 2.5, evaluations: 4 }, { fundamentalId: 'player-owner', average: 2.4, evaluations: 4 }, { fundamentalId: 'communicator', average: 2.3, evaluations: 4 }, { fundamentalId: 'team-builder', average: 2.4, evaluations: 4 }] },
    { month: '2025-09', overallAverage: 2.7, evaluations: 5, attitudes: [], fundamentals: [{ fundamentalId: 'learner', average: 2.8, evaluations: 5 }, { fundamentalId: 'player-owner', average: 2.6, evaluations: 5 }, { fundamentalId: 'communicator', average: 2.7, evaluations: 5 }, { fundamentalId: 'team-builder', average: 2.7, evaluations: 5 }] },
    { month: '2025-10', overallAverage: 2.9, evaluations: 6, attitudes: [], fundamentals: [{ fundamentalId: 'learner', average: 3.0, evaluations: 6 }, { fundamentalId: 'player-owner', average: 2.8, evaluations: 6 }, { fundamentalId: 'communicator', average: 2.9, evaluations: 6 }, { fundamentalId: 'team-builder', average: 2.9, evaluations: 6 }] },
    { month: '2025-11', overallAverage: 3.1, evaluations: 6, attitudes: [], fundamentals: [{ fundamentalId: 'learner', average: 3.2, evaluations: 6 }, { fundamentalId: 'player-owner', average: 3.0, evaluations: 6 }, { fundamentalId: 'communicator', average: 3.1, evaluations: 6 }, { fundamentalId: 'team-builder', average: 3.1, evaluations: 6 }] },
    { month: '2025-12', overallAverage: 3.3, evaluations: 7, attitudes: [], fundamentals: [{ fundamentalId: 'learner', average: 3.4, evaluations: 7 }, { fundamentalId: 'player-owner', average: 3.2, evaluations: 7 }, { fundamentalId: 'communicator', average: 3.3, evaluations: 7 }, { fundamentalId: 'team-builder', average: 3.3, evaluations: 7 }] },
    { month: '2026-01', overallAverage: 3.4, evaluations: 8, attitudes: [], fundamentals: [{ fundamentalId: 'learner', average: 3.5, evaluations: 8 }, { fundamentalId: 'player-owner', average: 3.3, evaluations: 8 }, { fundamentalId: 'communicator', average: 3.4, evaluations: 8 }, { fundamentalId: 'team-builder', average: 3.4, evaluations: 8 }] },
  ],
};

/**
 * Historial mensual de puntajes del usuario (gráfica de evolución).
 *
 * `undefined` significa 204: la persona todavía no acumula historial. Cualquier otro
 * fallo se propaga en vez de devolverse como historial vacío, porque un servicio caído
 * y una persona sin datos exigen mensajes distintos en pantalla.
 */
export function usePersonalGrowth(userId?: string) {
  return useQuery<PersonalGrowthResponse | null, Error, PersonalGrowthResponse | undefined>({
    queryKey: ['personalGrowth', userId],
    retry: retryOnTransientError,
    // TanStack Query 5 rechaza un queryFn que resuelve `undefined`: el 204 viaja como `null`
    // y vuelve a `undefined` en `select`, que es lo que esperan los consumidores.
    select: growth => growth ?? undefined,
    queryFn: async () => {
      if (USE_MOCK) return MOCK_GROWTH;
      try {
        return (await analyticsApi.getPersonalGrowth(userId)) ?? null;
      } catch (apiError: unknown) {
        reportApiError('analytics.personalGrowth', apiError);
        throw apiError;
      }
    },
  });
}
